import { useEffect, useState } from "react";
import { Loader2, KeyRound, Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatError } from "@/lib/api";

export const BackupCodes = ({ codes, onContinue }) => (
  <div data-testid="mfa-backup-codes" className="space-y-4">
    <p className="text-sm">Save these 10 backup codes somewhere safe. Each one works once if you lose your phone. They won't be shown again.</p>
    <div className="grid grid-cols-2 gap-2 font-mono text-sm rounded-lg bg-slate-100 dark:bg-slate-800 p-4">{codes.map((c) => <span key={c}>{c}</span>)}</div>
    <div className="flex gap-3">
      <Button type="button" variant="outline" data-testid="mfa-copy-codes-btn" onClick={() => { navigator.clipboard?.writeText(codes.join("\n")); toast.success("Codes copied"); }} className="rounded-full"><Copy size={14} className="mr-1" /> Copy</Button>
      {onContinue && <Button type="button" data-testid="mfa-codes-continue-btn" onClick={onContinue} className="rounded-full flex-1">I've saved them, continue</Button>}
    </div>
  </div>
);

const useSubmit = () => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = async (fn) => {
    setBusy(true);
    setError("");
    try { await fn(); } catch (e) { setError(formatError(e)); } finally { setBusy(false); }
  };
  return { busy, error, run };
};

export const MfaVerify = ({ mfaToken, onDone }) => {
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState(false);
  const [trust, setTrust] = useState(false);
  const { busy, error, run } = useSubmit();
  const submit = (e) => { e.preventDefault(); run(async () => onDone((await api.post("/auth/mfa/verify", { mfa_token: mfaToken, code, trust_device: trust })).data)); };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="mfa-verify-form">
      <p className="text-sm text-slate-600 dark:text-slate-300 flex gap-2"><KeyRound size={16} className="text-emerald-600 shrink-0" />{backup ? "Enter one of your backup codes." : "Enter the 6-digit code from your authenticator app."}</p>
      <Input data-testid="mfa-code-input" value={code} onChange={(e) => setCode(e.target.value.trim())} inputMode={backup ? "text" : "numeric"} autoComplete="one-time-code" placeholder={backup ? "ABCD-1234" : "123456"} className="h-11 font-mono tracking-widest" />
      {error && <p data-testid="mfa-error" className="text-sm text-red-600">{error}</p>}
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" data-testid="mfa-trust-device-checkbox" checked={trust} onChange={(e) => setTrust(e.target.checked)} className="h-4 w-4 mt-0.5 accent-emerald-600" />
        <span>Trust this device for 30 days<span className="block text-xs text-slate-500">Only on your own device. We'll ask again if your password changes.</span></span>
      </label>
      <Button data-testid="mfa-verify-btn" type="submit" disabled={busy || code.length < 6} className="w-full h-11 rounded-full">{busy && <Loader2 size={16} className="animate-spin mr-2" />}Verify</Button>
      <button type="button" data-testid="mfa-use-backup-btn" onClick={() => { setBackup(!backup); setCode(""); }} className="text-sm underline text-slate-600 dark:text-slate-300">{backup ? "Use my authenticator app" : "Use a backup code"}</button>
    </form>
  );
};

export const MfaSetup = ({ mfaToken, onDone }) => {
  const [qr, setQr] = useState(null);
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const { busy, error, run } = useSubmit();
  useEffect(() => { run(async () => setQr((await api.post("/auth/mfa/setup/start", { mfa_token: mfaToken || null })).data)); }, [mfaToken]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = (e) => { e.preventDefault(); run(async () => setResult((await api.post("/auth/mfa/setup/confirm", { mfa_token: mfaToken || null, code })).data)); };
  if (result) return <BackupCodes codes={result.backup_codes} onContinue={() => onDone(result)} />;
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="mfa-setup-form">
      <p className="text-sm text-slate-600 dark:text-slate-300">Scan this QR code with an authenticator app (Google Authenticator, Microsoft Authenticator or 1Password), then enter the 6-digit code it shows.</p>
      {qr ? <img src={qr.qr} alt="Authenticator QR code" data-testid="mfa-qr" className="w-44 h-44 rounded-lg border border-slate-200 bg-white" /> : <Loader2 className="animate-spin" />}
      {qr && <p className="text-xs text-slate-500">Can't scan? Enter this key: <span data-testid="mfa-secret" className="font-mono break-all">{qr.secret}</span></p>}
      <Input data-testid="mfa-setup-code-input" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="123456" className="h-11 font-mono tracking-widest" />
      {error && <p data-testid="mfa-setup-error" className="text-sm text-red-600">{error}</p>}
      <Button data-testid="mfa-setup-confirm-btn" type="submit" disabled={busy || code.length !== 6} className="w-full h-11 rounded-full">{busy && <Loader2 size={16} className="animate-spin mr-2" />}Turn on two-step sign-in</Button>
    </form>
  );
};
